export const COOKIE_CONSENT_KEY = "cherrfy_cookie_consent_v1";
export const COOKIE_CONSENT_EVENT = "cherrfy:cookie-consent-change";
export const OPEN_PRIVACY_SETTINGS_EVENT = "cherrfy:open-privacy-settings";
let memoryConsent = null;

export const COOKIE_CONSENT = {
  ACCEPTED: "accepted",
  DENIED: "denied",
};

export const PRIVACY_MODE = {
  PERSONALIZED: "personalized",
  NECESSARY_ONLY: "necessary_only",
};

export const RECENT_PRODUCT_IDS_KEY = "cherrfy_recent_product_ids_v1";
export const SESSION_SEARCH_HISTORY_KEY = "cherrfy_session_search_history_v1";

const NECESSARY_SCOPED_KEYS = ["cart", "user_settings"];
const SESSION_ONLY_KEYS = ["checkout_state", SESSION_SEARCH_HISTORY_KEY];
const PERSONALIZATION_STORAGE_KEYS = [
  "cherrfy_visitor_id",
  "cherrfy_device_id",
  "search_history_v1",
  "search_device_id_v1",
  "cherrfy_categories_recent",
  "cherrfy_guest_mix_seed",
  RECENT_PRODUCT_IDS_KEY,
  SESSION_SEARCH_HISTORY_KEY,
];
const DISALLOWED_PERSISTENT_STORAGE_KEYS = [
  "cherrfy_device_id",
  "search_history_v1",
  "search_device_id_v1",
  "cherrfy_categories_recent",
  "cherrfy_guest_mix_seed",
];

const getStorage = (type) => {
  try { return typeof window !== "undefined" ? window[type] : null; }
  catch { return null; }
};

const safeParseJson = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const readStorageItem = (storage, key) => {
  try {
    return storage?.getItem?.(key) || "";
  } catch {
    return "";
  }
};

const writeStorageItem = (storage, key, value) => {
  if (!storage) return false;
  try {
    if (value === undefined || value === null || value === "") storage?.removeItem?.(key);
    else storage?.setItem?.(key, String(value));
    return true;
  } catch {
    // Storage may be blocked by the browser.
    return false;
  }
};

const removeStorageItem = (storage, key) => {
  try {
    storage?.removeItem?.(key);
  } catch {
    // Storage may be blocked by the browser.
  }
};

const getLocalStorage = () => getStorage("localStorage");
const getSessionStorage = () => getStorage("sessionStorage");

const setFirstPartyConsentCookie = (value) => {
  if (typeof document === "undefined") return;
  const maxAge = 60 * 60 * 24 * 180;
  const secure = typeof window !== "undefined" && window.location?.protocol === "https:" ? "; Secure" : "";
  try { document.cookie = `${COOKIE_CONSENT_KEY}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`; } catch { /* Browser storage can be disabled. */ }
};

const removeFirstPartyConsentCookie = () => {
  if (typeof document === "undefined") return;
  const secure = typeof window !== "undefined" && window.location?.protocol === "https:" ? "; Secure" : "";
  try { document.cookie = `${COOKIE_CONSENT_KEY}=; Max-Age=0; Path=/; SameSite=Lax${secure}`; } catch { /* Browser storage can be disabled. */ }
};

const readFirstPartyConsentCookie = () => {
  try {
  if (typeof document === "undefined") return null;
  const row = String(document.cookie || "")
    .split(";")
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(`${COOKIE_CONSENT_KEY}=`));
  if (!row) return null;
  const value = decodeURIComponent(row.slice(COOKIE_CONSENT_KEY.length + 1));
  return value === COOKIE_CONSENT.ACCEPTED || value === COOKIE_CONSENT.DENIED ? value : null;
  } catch { return null; }
};

export const getCookieConsent = () => {
  const cookieValue = readFirstPartyConsentCookie();
  if (cookieValue) return cookieValue;
  try {
    const value = readStorageItem(getLocalStorage(), COOKIE_CONSENT_KEY).trim();
    if (value === COOKIE_CONSENT.ACCEPTED || value === COOKIE_CONSENT.DENIED) {
      setFirstPartyConsentCookie(value);
      return value;
    }
    return memoryConsent;
  } catch {
    return memoryConsent;
  }
};

export const hasPersonalizationConsent = () => getCookieConsent() === COOKIE_CONSENT.ACCEPTED;
export const getPrivacyMode = () =>
  hasPersonalizationConsent() ? PRIVACY_MODE.PERSONALIZED : PRIVACY_MODE.NECESSARY_ONLY;

const promoteScopedKey = (key) => {
  const local = getLocalStorage();
  const session = getSessionStorage();
  if (!local || !session) return;
  const sessionValue = readStorageItem(session, key);
  if (sessionValue) {
    if (writeStorageItem(local, key, sessionValue)) removeStorageItem(session, key);
  }
};

const syncNecessaryScopedStorage = (accepted) => {
  // Necessary functional storage (cart, settings) is consent-independent:
  // promote legacy session copies on accept, but never delete persistent copies
  // on deny — denying analytics consent must not wipe a shopper's cart.
  if (accepted) {
    for (const key of NECESSARY_SCOPED_KEYS) {
      promoteScopedKey(key);
    }
  }
};

const clearDisallowedPersistentStorage = () => {
  const local = getLocalStorage();
  for (const key of DISALLOWED_PERSISTENT_STORAGE_KEYS) {
    removeStorageItem(local, key);
  }
};

export const readScopedJson = (key, fallback = null, { persistWhenAccepted = true } = {}) => {
  const session = getSessionStorage();
  const local = getLocalStorage();
  const accepted = hasPersonalizationConsent();

  if (NECESSARY_SCOPED_KEYS.includes(key)) {
    const localValue = readStorageItem(local, key);
    if (localValue) return safeParseJson(localValue, fallback);
    const sessionValue = readStorageItem(session, key);
    if (sessionValue) {
      // Migrate a demoted copy back to persistent storage.
      if (writeStorageItem(local, key, sessionValue)) removeStorageItem(session, key);
      return safeParseJson(sessionValue, fallback);
    }
    return safeParseJson("", fallback);
  }

  const primary = accepted && persistWhenAccepted ? local || session : session;
  const secondary = accepted && persistWhenAccepted ? session : null;
  const primaryValue = readStorageItem(primary, key);
  if (primaryValue) return safeParseJson(primaryValue, fallback);
  const secondaryValue = readStorageItem(secondary, key);
  return safeParseJson(secondaryValue, fallback);
};

export const writeScopedJson = (key, value, { persistWhenAccepted = true } = {}) => {
  const session = getSessionStorage();
  const local = getLocalStorage();
  const serialized = JSON.stringify(value);
  const accepted = hasPersonalizationConsent();

  if (NECESSARY_SCOPED_KEYS.includes(key)) {
    if (writeStorageItem(local, key, serialized)) {
      removeStorageItem(session, key);
    } else {
      writeStorageItem(session, key, serialized);
    }
    return;
  }

  if (accepted && persistWhenAccepted && writeStorageItem(local, key, serialized)) {
    removeStorageItem(session, key);
    return;
  }

  writeStorageItem(session, key, serialized);
  if (local && key !== COOKIE_CONSENT_KEY) removeStorageItem(local, key);
};

export const removeScopedStorage = (key) => {
  removeStorageItem(getLocalStorage(), key);
  removeStorageItem(getSessionStorage(), key);
};

export const clearPersonalizationStorage = () => {
  const local = getLocalStorage();
  const session = getSessionStorage();
  for (const key of PERSONALIZATION_STORAGE_KEYS) {
    removeStorageItem(local, key);
    removeStorageItem(session, key);
  }
};

export const setCookieConsent = (value) => {
  const normalized = value === COOKIE_CONSENT.ACCEPTED ? COOKIE_CONSENT.ACCEPTED : COOKIE_CONSENT.DENIED;
  memoryConsent = normalized;
  const local = getLocalStorage();

  try {
    writeStorageItem(local, COOKIE_CONSENT_KEY, normalized);
    setFirstPartyConsentCookie(normalized);
    syncNecessaryScopedStorage(normalized === COOKIE_CONSENT.ACCEPTED);
    clearDisallowedPersistentStorage();
    if (normalized === COOKIE_CONSENT.DENIED) clearPersonalizationStorage();
    window.dispatchEvent(
      new CustomEvent(COOKIE_CONSENT_EVENT, {
        detail: { value: normalized },
      })
    );
  } catch {
    // Keep the app usable if storage is unavailable.
  }

  return normalized;
};

export const clearStoredPreferences = ({ includeConsent = false } = {}) => {
  clearPersonalizationStorage();
  removeScopedStorage("user_settings");
  for (const key of SESSION_ONLY_KEYS) removeScopedStorage(key);
  if (includeConsent) {
    memoryConsent = null;
    removeStorageItem(getLocalStorage(), COOKIE_CONSENT_KEY);
    removeFirstPartyConsentCookie();
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(COOKIE_CONSENT_EVENT, {
        detail: { value: includeConsent ? null : getCookieConsent(), cleared: true },
      })
    );
  }
};

export const subscribeCookieConsent = (callback) => {
  if (typeof window === "undefined" || typeof callback !== "function") {
    return () => {};
  }

  const handler = () => callback(getCookieConsent());
  const storageHandler = (event) => {
    if (event.key !== COOKIE_CONSENT_KEY && event.key !== null) return;
    memoryConsent = event.newValue === COOKIE_CONSENT.ACCEPTED || event.newValue === COOKIE_CONSENT.DENIED ? event.newValue : null;
    if (memoryConsent) setFirstPartyConsentCookie(memoryConsent);
    else removeFirstPartyConsentCookie();
    syncNecessaryScopedStorage(memoryConsent === COOKIE_CONSENT.ACCEPTED);
    if (memoryConsent !== COOKIE_CONSENT.ACCEPTED) clearPersonalizationStorage();
    handler();
  };
  window.addEventListener(COOKIE_CONSENT_EVENT, handler);
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(COOKIE_CONSENT_EVENT, handler);
    window.removeEventListener("storage", storageHandler);
  };
};

export const openPrivacySettings = () => {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(OPEN_PRIVACY_SETTINGS_EVENT));
};

export const makePersonalizationId = (prefix = "id") => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${prefix}_${Math.random().toString(36).slice(2)}_${Date.now()}`;
};

export const getOrCreatePersonalizationId = (storageKey, prefix) => {
  if (!hasPersonalizationConsent()) return "";
  const local = getLocalStorage();
  if (!local) return makePersonalizationId(prefix);

  try {
    const existing = local.getItem(storageKey);
    if (existing && String(existing).trim()) return String(existing).trim();
    const generated = makePersonalizationId(prefix);
    local.setItem(storageKey, generated);
    return generated;
  } catch {
    return makePersonalizationId(prefix);
  }
};

export const readRecentProductIds = (limit = 24) => {
  if (!hasPersonalizationConsent()) return [];
  const rows = readScopedJson(RECENT_PRODUCT_IDS_KEY, [], { persistWhenAccepted: true });
  return Array.isArray(rows)
    ? rows
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .slice(0, limit)
    : [];
};

export const recordRecentProductId = (productId, limit = 24) => {
  if (!hasPersonalizationConsent()) return [];
  const id = String(productId || "").trim();
  if (!id) return readRecentProductIds(limit);
  const next = [id, ...readRecentProductIds(limit).filter((value) => value !== id)].slice(0, limit);
  writeScopedJson(RECENT_PRODUCT_IDS_KEY, next, { persistWhenAccepted: true });
  return next;
};
